import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Button, Center, chakra, Checkbox, HStack, IconButton, Link, Spinner, Table, Text, VStack } from '@chakra-ui/react';
import { useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { BulkDownloadInvestorsButton } from '@src/components/sco/InvestorSearch/InvestorSearchResults/BulkDownloadInvestorsButton';
import { BulkSaveInvestorsButton } from '@src/components/sco/InvestorSearch/InvestorSearchResults/BulkSaveInvestorsButton';
import { InvestorResultRow } from '@src/components/sco/InvestorSearch/InvestorSearchResults/InvestorResultRow';
import { InvestorSearchFilters, InvestorSearchPagination, useInvestorSearchContext } from '@src/components/sco/InvestorSearch/Layout/InvestorSearchContext';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useDebounce } from '@uidotdev/usehooks';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useEffect, useState } from 'react';
import { BsList } from 'react-icons/bs';
export type InvestorSearchResultsProps = {
  filters?: InvestorSearchFilters;
  pagination?: InvestorSearchPagination;
  showBulkDownloadButton?: boolean;
  showBulkSaveButton?: boolean;
};
export const InvestorSearchResults = props => {
  const $ = _c(84);
  const layout = useParentAppLayout();
  const {
    currentUser
  } = useCurrentUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [checkedInvestorProfileIds, setCheckedInvestorProfileIds] = useState(t0);
  const [page, setPage] = useState(1);
  const [selectedInvestorProfileId, setSelectedInvestorProfileId] = useQueryState("investorProfileId", parseAsString);
  const {
    filterValues
  } = useInvestorSearchContext();
  let t1;
  if ($[1] !== filterValues || $[2] !== props.filters) {
    t1 = JSON.stringify({
      ...filterValues,
      ...(props.filters || {})
    });
    $[1] = filterValues;
    $[2] = props.filters;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const debouncedQueryVariables = useDebounce(t1, 300);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = gql`
      query (
        $hqDistanceInMiles: Int
        $hqLatitude: Float
        $hqLocation: String
        $hqLocationRegion: String
        $hqLocationCountry: String
        $hqLongitude: Float
        $investmentStages: [String]
        $investorTypes: [String]
        $isSavedByUser: Boolean
        $isVerified: Boolean
        $keywords: String
        $matchAll: Boolean
        $maximumInvestmentInDollars: FriendlyNumeral
        $minimumInvestmentInDollars: FriendlyNumeral
        $order: [[String]]
        $page: Int
        $pageSize: Int
        $preferredIndustries: [String]
        $preferredIndustriesAndVerticals: [String]
        $preferredVerticals: [String]
      ) {
        scoInvestorProfiles(
          hqDistanceInMiles: $hqDistanceInMiles
          hqLatitude: $hqLatitude
          hqLocation: $hqLocation
          hqLocationRegion: $hqLocationRegion
          hqLocationCountry: $hqLocationCountry
          hqLongitude: $hqLongitude
          investmentStages: $investmentStages
          investorTypes: $investorTypes
          isSavedByUser: $isSavedByUser
          isVerified: $isVerified
          keywords: $keywords
          matchAll: $matchAll
          maximumInvestmentInDollars: $maximumInvestmentInDollars
          minimumInvestmentInDollars: $minimumInvestmentInDollars
          order: $order
          page: $page
          pageSize: $pageSize
          preferredIndustries: $preferredIndustries
          preferredIndustriesAndVerticals: $preferredIndustriesAndVerticals
          preferredVerticals: $preferredVerticals
        ) {
          id
          items {
            id
            investorProfileId
            currentUserHasSaved
            hqLocation
            hqLocationCity
            hqLocationCountry
            hqLocationState
            isVerified
            logoUrl
            maximumInvestmentInDollars
            maximumDealSizeInDollars
            minimumInvestmentInDollars
            minimumDealSizeInDollars
            preferredIndustries
            preferredVerticals
            preferredIndustriesAndVerticals
            primaryInvestorType
            investmentStages
            title
          }
          pagination {
            itemCount
            hasNextPage
          }
        }
      }
    `;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = !currentUser?.contentPermissions?.canAccessFGInvestorContacts;
  let t4;
  if ($[5] !== debouncedQueryVariables) {
    t4 = JSON.parse(debouncedQueryVariables);
    $[5] = debouncedQueryVariables;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = {
      ...t4
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = {
      ssr: false,
      skip: t3,
      variables: t5
    };
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const query = useQuery(t2, t6);
  let t7;
  if ($[12] !== currentUser?.contentPermissions?.canAccessFGInvestorContacts || $[13] !== query) {
    t7 = () => {
      if (!query.loading && currentUser?.contentPermissions?.canAccessFGInvestorContacts) {
        query.refetch();
      }
    };
    $[12] = currentUser?.contentPermissions?.canAccessFGInvestorContacts;
    $[13] = query;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = currentUser?.contentPermissions?.canAccessFGInvestorContacts;
  let t9;
  if ($[15] !== t8) {
    t9 = [t8];
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  useEffect(t7, t9);
  let t10;
  if ($[17] !== query.data?.scoInvestorProfiles?.items) {
    t10 = query.data?.scoInvestorProfiles?.items || [];
    $[17] = query.data?.scoInvestorProfiles?.items;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const investors = t10;
  let t11;
  if ($[19] !== query.data?.scoInvestorProfiles?.pagination) {
    t11 = query.data?.scoInvestorProfiles?.pagination || {};
    $[19] = query.data?.scoInvestorProfiles?.pagination;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const pagination = t11;
  let t12;
  if ($[21] !== layout) {
    t12 = () => {
      layout.togglePanel("investor-search-nav");
    };
    $[21] = layout;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <BsList />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12) {
    t14 = <IconButton aria-label="Toggle Navigation" bgColor="colorPalette.bg/50" borderRadius={4} marginRight="1em" size="sm" variant="ghost" onClick={t12}>{t13}</IconButton>;
    $[24] = t12;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== pagination?.itemCount || $[27] !== query.loading) {
    t15 = pagination?.itemCount ? pagination?.itemCount?.toLocaleString() : query.loading ? "Loading" : "No";
    $[26] = pagination?.itemCount;
    $[27] = query.loading;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15) {
    t16 = <chakra.span>{t15}{" "}Matches</chakra.span>;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== checkedInvestorProfileIds.length) {
    t17 = checkedInvestorProfileIds.length > 0 && <chakra.span color="colorPalette.fg/50" fontWeight="normal">{" \u2022 "}{checkedInvestorProfileIds.length}{" Selected"}{" "}<Link variant="underline" onClick={() => setCheckedInvestorProfileIds([])}>Clear</Link></chakra.span>;
    $[31] = checkedInvestorProfileIds.length;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t14 || $[34] !== t16 || $[35] !== t17) {
    t18 = <Table.ColumnHeader color="colorPalette.fg" colSpan={4} textStyle="label">{t14}{t16}{t17}</Table.ColumnHeader>;
    $[33] = t14;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== checkedInvestorProfileIds || $[38] !== currentUser || $[39] !== props.showBulkDownloadButton) {
    t19 = (props.showBulkDownloadButton || currentUser.isAdmin) && <BulkDownloadInvestorsButton investorProfileIds={checkedInvestorProfileIds} />;
    $[37] = checkedInvestorProfileIds;
    $[38] = currentUser;
    $[39] = props.showBulkDownloadButton;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== checkedInvestorProfileIds || $[42] !== props.showBulkSaveButton) {
    t20 = props.showBulkSaveButton && <BulkSaveInvestorsButton investorProfileIds={checkedInvestorProfileIds} />;
    $[41] = checkedInvestorProfileIds;
    $[42] = props.showBulkSaveButton;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t19 || $[45] !== t20) {
    t21 = <Table.ColumnHeader><HStack gap="1em" justifyContent="flex-end" width="full">{t19}{t20}</HStack></Table.ColumnHeader>;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t18 || $[48] !== t21) {
    t22 = <Table.Row>{t18}{t21}</Table.Row>;
    $[47] = t18;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== investors) {
    t23 = ev => {
      if (ev.target.checked) {
        setCheckedInvestorProfileIds(investors.map(_temp));
      } else {
        setCheckedInvestorProfileIds([]);
      }
    };
    $[50] = investors;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Checkbox.HiddenInput />;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t23) {
    t26 = <Table.ColumnHeader><Checkbox.Root onCheckedChange={t23}>{t24}{t25}</Checkbox.Root></Table.ColumnHeader>;
    $[54] = t23;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  let t28;
  let t29;
  let t30;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Table.ColumnHeader color="colorPalette.fg" textStyle="label">Investor</Table.ColumnHeader>;
    t28 = <Table.ColumnHeader color="colorPalette.fg" textStyle="label">Location</Table.ColumnHeader>;
    t29 = <Table.ColumnHeader color="colorPalette.fg" textStyle="label">Investment Size/Stage</Table.ColumnHeader>;
    t30 = <Table.ColumnHeader color="colorPalette.fg" textStyle="label">Industries / Verticals</Table.ColumnHeader>;
    $[56] = t27;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    t27 = $[56];
    t28 = $[57];
    t29 = $[58];
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t26) {
    t31 = <Table.Row>{t26}{t27}{t28}{t29}{t30}</Table.Row>;
    $[60] = t26;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== t22 || $[63] !== t31) {
    t32 = <Table.Header>{t22}{t31}</Table.Header>;
    $[62] = t22;
    $[63] = t31;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] !== checkedInvestorProfileIds || $[66] !== investors || $[67] !== selectedInvestorProfileId || $[68] !== setSelectedInvestorProfileId) {
    let t34;
    if ($[70] !== checkedInvestorProfileIds || $[71] !== selectedInvestorProfileId || $[72] !== setSelectedInvestorProfileId) {
      t34 = investor => <InvestorResultRow key={investor.id} investor={investor} isChecked={checkedInvestorProfileIds.includes(investor.investorProfileId)} isSelected={selectedInvestorProfileId == investor.investorProfileId} onSelect={investorProfileId => setSelectedInvestorProfileId(investorProfileId)} onCheckedChange={(investorProfileId_0, isChecked) => isChecked ? setCheckedInvestorProfileIds([...checkedInvestorProfileIds, investorProfileId_0]) : setCheckedInvestorProfileIds(checkedInvestorProfileIds.filter(x => x !== investorProfileId_0))} />;
      $[70] = checkedInvestorProfileIds;
      $[71] = selectedInvestorProfileId;
      $[72] = setSelectedInvestorProfileId;
      $[73] = t34;
    } else {
      t34 = $[73];
    }
    t33 = investors.map(t34);
    $[65] = checkedInvestorProfileIds;
    $[66] = investors;
    $[67] = selectedInvestorProfileId;
    $[68] = setSelectedInvestorProfileId;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[74] !== t33) {
    t34 = <Table.Body>{t33}</Table.Body>;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== page || $[77] !== pagination?.hasNextPage || $[78] !== query) {
    t35 = <Table.Footer><Table.Row><Table.Cell colSpan={100}><Center padding="1em"><PaywalledAction contentPermission="canAccessFGInvestorContacts" variant="button-only">{query.loading ? <Spinner /> : <VStack><Button disabled={!pagination?.hasNextPage} onClick={() => {
                  query.fetchMore({
                    variables: {
                      page: page + 1
                    }
                  });
                  setPage(page + 1);
                }}>{pagination?.hasNextPage ? "Load More" : "All Loaded"}</Button>{!pagination?.hasNextPage && <Text opacity={0.5}>You can try expanding your filters for more results.</Text>}</VStack>}</PaywalledAction></Center></Table.Cell></Table.Row></Table.Footer>;
    $[76] = page;
    $[77] = pagination?.hasNextPage;
    $[78] = query;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== t32 || $[81] !== t34 || $[82] !== t35) {
    t36 = <PaywalledAction contentPermission="canAccessFGInvestorContacts" variant="overlay"><Table.ScrollArea height="full" overflowY="scroll" width="full"><Table.Root variant="outline">{t32}{t34}{t35}</Table.Root></Table.ScrollArea></PaywalledAction>;
    $[80] = t32;
    $[81] = t34;
    $[82] = t35;
    $[83] = t36;
  } else {
    t36 = $[83];
  }
  return t36;
};
function _temp(i) {
  return i.investorProfileId;
}